import React, { useEffect, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { Button } from "components/Graphs/Button";
import LoadingText from "components/Graphs/LoadingText";

export const TerminalBlock: React.FC<{ command: string }> = ({ command }) => (
  <div className="overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
    <div className="flex items-start gap-2 px-3 py-2.5">
      <span className="shrink-0 select-none font-mono text-sm leading-[1.7] text-highlight">
        $
      </span>
      <code className="flex-1 whitespace-pre-wrap break-all font-mono text-[12px] leading-[1.7] text-zinc-200">
        {command}
      </code>
    </div>
  </div>
);

export const CopyCommandButton: React.FC<{
  copied: boolean;
  disabled?: boolean;
  onClick: () => void;
  variant?: "primary" | "secondary";
}> = ({ copied, disabled = false, onClick, variant = "primary" }) => (
  <Button
    type="button"
    onClick={onClick}
    disabled={disabled}
    variant={variant}
    className="mt-2.5 w-full gap-[7px]"
  >
    {copied ? (
      <>
        <CheckIcon className="h-4 w-4" />
        Copied
      </>
    ) : (
      <>
        <CopyIcon className="h-4 w-4" />
        Copy command
      </>
    )}
  </Button>
);

const waitingDeviceImages = [
  { src: "/assets/spark.svg", alt: "DGX Spark" },
  { src: "/assets/station.svg", alt: "DGX Station" },
  { src: "/assets/laptop.svg", alt: "Laptop" },
  { src: "/assets/server.svg", alt: "Server" },
];

const RETRY_COUNTDOWN_SECONDS = 10;

const RetryCountdownText: React.FC = () => {
  const [retryCountdown, setRetryCountdown] = useState(RETRY_COUNTDOWN_SECONDS);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRetryCountdown((previousCountdown) =>
        previousCountdown === 1
          ? RETRY_COUNTDOWN_SECONDS
          : previousCountdown - 1,
      );
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  return (
    <>
      <div className="text-red-800">Unable to load devices.</div>
      <div>Retrying in {retryCountdown} seconds.</div>
    </>
  );
};

export const WaitingForDevice: React.FC<{
  withStatus: boolean;
  isPollingError?: boolean;
}> = ({ withStatus, isPollingError = false }) => (
  <div
    className={`w-full max-w-[700px] animate-fade-in-240 ${
      withStatus
        ? "lg:flex lg:min-h-[260px] lg:max-w-none lg:items-center lg:justify-center lg:self-center"
        : ""
    }`}
  >
    <div
      className={`flex items-center gap-[14px] pb-1 pt-[18px] ${
        withStatus
          ? "lg:flex-col lg:items-center lg:justify-center lg:gap-[18px] lg:p-0 lg:text-center"
          : ""
      }`}
    >
      <div className="text-sm font-medium text-white">
        <LoadingText
          text="Waiting for your device to connect..."
          className="pb-2 text-lg"
        />
      </div>
      <div
        className={`relative h-[50px] w-[76px] shrink-0 ${
          withStatus ? "lg:h-[106px] lg:w-[168px]" : ""
        }`}
        aria-hidden="true"
      >
        {waitingDeviceImages.map((device, index) => (
          <img
            key={device.src}
            src={device.src}
            alt={device.alt}
            className={`absolute inset-0 h-full w-full origin-center object-contain opacity-0 animate-device-cycle motion-reduce:animate-none motion-reduce:first:transform-none motion-reduce:first:opacity-100 ${
              index === 0
                ? "[animation-delay:0s]"
                : index === 1
                  ? "[animation-delay:2s]"
                  : index === 2
                    ? "[animation-delay:4s]"
                    : "[animation-delay:6s]"
            }`}
          />
        ))}
      </div>
      <div
        className="mt-1.5 text-sm text-zinc-500"
      >
        {isPollingError ? (
          <RetryCountdownText />
        ) : (
          "This page will update when the device connects."
        )}
      </div>
    </div>
  </div>
);

export const StepHeading: React.FC<{
  number: number;
  children: React.ReactNode;
  isReady?: boolean;
  highlightWhenReady?: boolean;
  labelId?: string;
}> = ({
  number,
  children,
  isReady = false,
  highlightWhenReady = false,
  labelId,
}) => (
  <div className="flex items-center gap-3">
    <div
      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-sm font-bold transition-colors duration-150 ${
        isReady && highlightWhenReady ? "text-highlightDarker" : "text-white"
      }`}
    >
      {number}
    </div>
    <h2
      className={`flex items-center gap-2 text-base font-medium transition-colors duration-150 ${
        isReady && highlightWhenReady ? "text-highlightDarker" : "text-white"
      }`}
    >
      <span id={labelId}>{children}</span>
      {isReady ? (
        <svg
          aria-label="Complete"
          className="h-4 w-4 animate-step-check text-highlight motion-reduce:animate-none"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="m3.25 8.25 3 3 6.5-6.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </h2>
  </div>
);
